import React, { useEffect, useState, useContext } from 'react';
import { Badge, Box, Flex, Heading, Text, Image, Button, VStack } from '@chakra-ui/react';
import { instrumentTexts } from "../i18n/instruments";
import { contractTexts } from "../i18n/contracts";
import { LanguageContext } from "../contexts/LanguageContext";
const API_BASE_URL = process.env.REACT_APP_API_URL;

const STATUS_PALETTE = {
  Active: "green",
  Created: "orange",
  EndingSoon: "orange",
};

const SectionLabel = ({ children }) => (
  <Text
    fontFamily="space"
    fontSize="xs"
    letterSpacing="0.2em"
    textTransform="uppercase"
    color="gray.500"
    mb={2}
  >
    {children}
  </Text>
);

const SpecRow = ({ label, value }) => (
  <Flex justify="space-between" gap={3} py={1} borderBottomWidth="1px" borderColor="gray.100" _last={{ borderBottomWidth: 0 }}>
    <Text fontSize="sm" color="gray.500">{label}</Text>
    <Text fontSize="sm" color="gray.800" fontWeight="medium" textAlign="right">{value}</Text>
  </Flex>
);

const Contracts = () => {
  const [contracts, setContracts] = useState([]);

  const { language } = useContext(LanguageContext);
  const t = instrumentTexts[language] || instrumentTexts["et"];
  const ct = contractTexts[language] || contractTexts["et"];

  useEffect(() => {
    const fetchContracts = async () => {
      const response = await fetch(`${API_BASE_URL}/api/contracts/`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    };
    fetchContracts();
  }, []);

  // Function to handle contract PDF download
  const handleDownload = async (contractId) => {
    const response = await fetch(`${API_BASE_URL}/api/contracts/download/${contractId}/`, {
      credentials: 'include',
    });
    if (response.ok) {
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Contract_${contractId}.pdf`;
      link.click();
    } else {
      console.error('Error downloading the contract.');
    }
  };

  return (
    <Box minH="calc(100vh - 4rem)" bg="gray.50" py={{ base: 8, md: 12 }} px={4}>
      <Box maxW="5xl" mx="auto">
        <Box textAlign="center" mb={8}>
          <Text
            fontFamily="space"
            fontSize="xs"
            letterSpacing="0.3em"
            textTransform="uppercase"
            color="gray.500"
            mb={2}
          >
            akordion.ee
          </Text>
          <Heading fontFamily="heading" fontWeight="500" fontSize="3xl" color="gray.900" textTransform="capitalize">
            {ct.contracts.toLowerCase()}
          </Heading>
        </Box>

        <VStack gap={6} align="stretch">
          {contracts.map((contract, index) => (
            <Box
              key={index}
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="sm"
              p={{ base: 5, md: 8 }}
            >
              {/* Header */}
              <Flex align="baseline" gap={3} wrap="wrap" mb={5}>
                <Heading fontFamily="heading" fontWeight="600" fontSize="2xl" color="gray.900">
                  {ct.contract} #{contract.agreement.agreementId}
                </Heading>
                <Badge
                  colorPalette={STATUS_PALETTE[contract.agreement.status] || "gray"}
                  variant="subtle"
                  fontFamily="space"
                  fontSize="10px"
                  letterSpacing="0.15em"
                  textTransform="uppercase"
                  px={2}
                  py={0.5}
                  borderRadius="sm"
                >
                  {t.status[contract.agreement.status] || contract.agreement.status}
                </Badge>
              </Flex>

              <Flex gap={{ base: 5, md: 8 }} direction={{ base: "column", md: "row" }}>
                {/* Image */}
                <Box
                  bg="gray.100"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.200"
                  overflow="hidden"
                  flexShrink={0}
                  alignSelf={{ base: "center", md: "flex-start" }}
                  w={{ base: "200px", md: "220px" }}
                  h="180px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src={`${API_BASE_URL}${contract.imageLink}`}
                    alt={`${contract.model.brand} ${contract.model.model}`}
                    objectFit="contain"
                    h="100%"
                  />
                </Box>

                {/* Info grid */}
                <Box
                  flex="1"
                  display="grid"
                  gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={{ base: 5, md: 6 }}
                >
                  {/* Agreement */}
                  <Box>
                    <SectionLabel>{t.headings.d}</SectionLabel>
                    <SpecRow
                      label={ct.contract}
                      value={t.status[contract.agreement.status] || contract.agreement.status}
                    />
                    <SpecRow label={ct.reference} value={contract.agreement.referenceNr} />
                    <SpecRow label={ct.rate} value={`${Math.round(contract.agreement.rate * 100) / 100}€`} />
                    <SpecRow label={ct.start} value={contract.agreement.startDate} />
                    <SpecRow label={ct.return} value={contract.agreement.endDate} />
                  </Box>

                  {/* Model + instrument */}
                  <Box>
                    <SectionLabel>{t.headings.a}</SectionLabel>
                    <SpecRow label={t.labels.brand} value={contract.model.brand} />
                    <SpecRow label={t.labels.model} value={contract.model.model} />
                    <SpecRow label={t.keys} value={contract.model.keys} />
                    <SpecRow label={t.basses} value={contract.model.sb} />
                    <SpecRow label={t.labels.serial} value={contract.instrument.serial} />
                    <SpecRow
                      label={t.color}
                      value={t.colors[contract.instrument.color] || contract.instrument.color}
                    />
                  </Box>
                </Box>
              </Flex>

              {/* Actions */}
              <Flex justify={{ base: "center", md: "flex-end" }} mt={6}>
                <Button
                  onClick={() => handleDownload(contract.agreement.agreementId)}
                  size="sm"
                  bg="#09090b"
                  color="white"
                  borderRadius="sm"
                  fontFamily="space"
                  fontSize="xs"
                  fontWeight="500"
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                  _hover={{ bg: "gray.800" }}
                  px={6}
                >
                  {ct.download}
                </Button>
              </Flex>
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
};

export default Contracts;
